import { Box, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import type { FC } from "react"
import React from "react"

import { FilterChip } from "@/components/common/Filters/FilterChip"
import { DATE_FORMAT } from "@/constants/formats"
import type { PriceChangeFilterInput, PriceChangeType } from "@/graphql/codegen/graphql"
import CategoryFilterChips from "@/screens/Pricing/components/filterChips/CategoryFilterChips"
import ClassFilterChips from "@/screens/Pricing/components/filterChips/ClassFilterChips"
import CustomerFilterChips from "@/screens/Pricing/components/filterChips/CustomerFilterChips"
import SpecificProductFilterChips from "@/screens/Pricing/components/filterChips/SpecificProductFilterChips"
import SupplierFilterChips from "@/screens/Pricing/components/filterChips/SupplierFilterChips"
import { priceChangeTypeLabelMap } from "@/screens/Pricing/constants/priceChangeLabelMap"

const PriceChangeFilterChips: FC = () => {
  const {
    changeType = "",
    supplierId = "",
    customerIds = [],
    effectiveDate = "",
    productCategoryId = "",
    productClassId = "",
    specificProductIds = [],
  } = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })
  const navigate = useNavigate({ from: "/app/pricing/price-change" })

  if (
    !changeType.length &&
    !supplierId.length &&
    !customerIds.length &&
    !effectiveDate.length &&
    !productClassId.length &&
    !productCategoryId.length &&
    !specificProductIds.length
  ) {
    return null
  }

  const handleClearFilter = (key: keyof PriceChangeFilterInput, value?: string | PriceChangeType) => {
    navigate({
      search: (prev: any) => {
        const newFilter = { ...prev, page: undefined }

        if (Array.isArray(newFilter[key])) {
          ;(newFilter[key] as (string[] | PriceChangeType)[]) = (
            newFilter[key] as (string[] | PriceChangeType)[]
          ).filter((item) => item !== value)

          if (newFilter[key].length === 0) {
            newFilter[key] = undefined
          }
        } else {
          newFilter[key] = value
        }

        return newFilter
      },
      replace: true,
    })
  }

  return (
    <Box data-testid='price-change-filter-chips' display='flex' flexWrap='wrap' alignItems='center' gap={2}>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {Boolean(changeType.length) && (
        <FilterChip
          key={`price-change-type-filter-chip`}
          label={`Change Type: ${priceChangeTypeLabelMap.get(changeType as PriceChangeType)}`}
          onDelete={() => handleClearFilter("changeType", undefined)}
        />
      )}

      {Boolean(effectiveDate.length) && (
        <FilterChip
          key={`price-change-effective-date-filter-chip`}
          label={`Effective Date: ${dayjs(effectiveDate).format(DATE_FORMAT.US)}`}
          onDelete={() => handleClearFilter("effectiveDate", undefined)}
        />
      )}

      {Boolean(supplierId.length) && <SupplierFilterChips selectedSupplier={supplierId} onRemove={handleClearFilter} />}
      {Boolean(productCategoryId.length) && (
        <CategoryFilterChips selectedCategory={productCategoryId} onRemove={handleClearFilter} />
      )}
      {Boolean(customerIds.length) && (
        <CustomerFilterChips selectedCustomerIds={customerIds} onRemove={handleClearFilter} />
      )}
      {Boolean(productClassId.length) && (
        <ClassFilterChips selectedClass={productClassId} onRemove={handleClearFilter} />
      )}
      {Boolean(specificProductIds.length) && (
        <SpecificProductFilterChips selectedProducts={specificProductIds} onRemove={handleClearFilter} />
      )}
    </Box>
  )
}
export default PriceChangeFilterChips
